import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  Calendar,
  Clock,
  MapPin,
  Coffee,
  ChevronLeft,
  ChevronRight,
  Briefcase
} from 'lucide-react';

export const EmployeeSchedule = () => {
  const { api } = useEmployeeAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/api/employee-portal/my-schedule');
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">My Schedule</h1>
        <p className="text-gray-500 text-sm">Your work schedule and breaks</p>
      </div>

      {/* Lunch Break Policy Card */}
      {schedule?.lunch_policy && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Coffee className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-900">1-Hour Paid Lunch Break</p>
              <p className="text-sm text-amber-700">
                Typical time: {schedule.lunch_policy.typical_time}
              </p>
              <p className="text-xs text-amber-600 mt-1">{schedule.lunch_policy.note}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Schedule List */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-[#C6A87C]" />
          <h2 className="font-semibold">Upcoming Schedule</h2>
        </div>

        {schedule?.schedule && schedule.schedule.length > 0 ? (
          <div className="space-y-3">
            {schedule.schedule.map((event, index) => (
              <div
                key={event.id || index}
                className="border border-gray-100 rounded-xl p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{event.title}</p>
                    <p className="text-sm text-gray-500">{event.description}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    event.event_type === 'job' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {event.event_type}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(event.start_date).toLocaleDateString()}
                  </div>
                  {event.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {event.location}
                    </div>
                  )}
                </div>

                {/* Lunch break indicator for job events */}
                {event.lunch_break && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Coffee className="w-4 h-4" />
                      <span className="text-sm">
                        {event.lunch_break.duration} lunch ({event.lunch_break.paid ? 'Paid' : 'Unpaid'})
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No scheduled events</p>
            <p className="text-sm text-gray-400">Check back later for updates</p>
          </div>
        )}
      </motion.div>

      {/* Work Day Info */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <h3 className="font-semibold mb-3">Typical Work Day</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Start Time</span>
            <span className="font-medium">7:00 AM</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Coffee className="w-4 h-4 text-amber-500" />
              <span className="text-gray-600">Lunch Break</span>
            </div>
            <span className="font-medium">12:00 PM - 1:00 PM</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">End Time</span>
            <span className="font-medium">4:00 PM</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-600">Total Work Hours</span>
            <span className="font-medium text-[#C6A87C]">8 hours (incl. paid lunch)</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EmployeeSchedule;
